<template>
  <FlowSelector :fields="fields" :bgColor="backgroundColor">
    <div v-if="showToggle" class="subnavigation-switch">
      <span class="switcher">
        <input
          tabindex="0"
          ref="toggle"
          @click="switchFlow"
          @keydown.space.prevent="switchFlow"
          type="checkbox"
          id="switcher-1"
          :data-first="leftText"
          :data-second="rightText"
          :checked="checked"
          :aria-checked="checked.toString()"
          role="switch"
          :aria-label="$t('switch-to') + ' ' + rightText"
        />
        <label class="text-m" for="switcher-1"></label>
      </span>
    </div>
    <div v-if="showMultiSwitch" class="multi-switch">
      <button
        v-for="(priceSwitch, i) in priceSwitches"
        :key="i"
        @click="selectPrice(i)"
        class="text-m"
        :aria-pressed="isCurrentPrice(priceSwitch) ? 'true' : 'false'"
      >
        {{ priceSwitch.fields.PriceSwitchText.value }}
      </button>
    </div>
  </FlowSelector>
</template>

<script>
import { mapGetters } from 'vuex';
import FlowSelector from './util/FlowSelector.vue';
import { gtmViewItemListEvent } from '@/gtmTracking';

export default {
  name: 'FlowSelectorSwitch',
  components: {
    FlowSelector,
  },
  props: {
    fields: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      currentPrice: 'config/getPriceKey',
    }),
    leftText() {
      return this.priceSwitches[0]?.fields?.PriceSwitchText?.value;
    },
    rightText() {
      return this.priceSwitches[1]?.fields?.PriceSwitchText?.value;
    },
    isDefault() {
      return this.$store.state.config?.useDefaultPrices ?? true;
    },
    checked() {
      return this.currentPrice === this.priceSwitches[1]?.fields?.PriceSwitchKey?.value;
    },
    backgroundColor() {
      if (this.fields.BackgroundColor.value) return this.fields.BackgroundColor.value;

      const index = this.priceSwitches.findIndex((priceSwitch) => priceSwitch.fields.PriceSwitchKey.value === this.currentPrice);
      return this.priceSwitches[index]?.fields?.PriceSwitchBackgroundColor?.value;
    },
    showToggle() {
      return this.priceSwitches.length === 2;
    },
    showMultiSwitch() {
      return this.priceSwitches.length > 2;
    },
    priceSwitches() {
      return this.fields.PriceSwitches;
    },
  },
  mounted() {
    if (!this.isDefault) {
      if (this.showToggle && !this.checked) this.switchFlow();

      if (this.showMultiSwitch) {
        // find the index of the current price switch
        const index = this.priceSwitches.findIndex(
          (priceSwitch) => priceSwitch.fields.PriceSwitchKey.value === this.currentPrice,
        );
        this.selectPrice(index);
      }
    }

    if (this.$store.state.config?.priceKeyDefault === this.priceSwitches[1]?.fields?.PriceSwitchKey?.value) {
      this.switchFlow();
    }

    // switch based on URL param
    const urlParamKey = this.$route.query.tab;
    if (urlParamKey) {
      const priceSwitch = this.priceSwitches.find(
        (priceSwitch) => priceSwitch.fields.PriceSwitchQueryParameterValue.value === urlParamKey,
      );
      if (priceSwitch && !this.isCurrentPrice(priceSwitch)) {
        if (this.showMultiSwitch) {
          const index = this.priceSwitches.findIndex(
            (i) => i.fields.PriceSwitchKey.value === priceSwitch.fields.PriceSwitchKey.value,
          );
          if (index >= 0) {
            this.selectPrice(index);
          }
        }

        if (this.showToggle) {
          setTimeout(() => {
            if (this.showToggle) {
              this.switchFlow();
            }
          }, 300);
        }
      }
    }
  },
  methods: {
    isCurrentPrice(priceSwitch) {
      return this.currentPrice === priceSwitch?.fields?.PriceSwitchKey?.value;
    },
    async switchFlow() {
      const newPrice = this.checked
        ? this.priceSwitches[0]?.fields?.PriceSwitchKey?.value
        : this.priceSwitches[1]?.fields?.PriceSwitchKey?.value;

      await this.$store.dispatch('config/updatePriceKey', newPrice);
      await this.$store.dispatch('config/updateRatePlanPrices');

      gtmViewItemListEvent();
    },
    async selectPrice(index) {
      if (this.currentPrice === this.priceSwitches[index]?.fields?.PriceSwitchKey?.value) return;

      const currentPrice = this.priceSwitches[index]?.fields?.PriceSwitchKey?.value;

      await this.$store.dispatch('config/updatePriceKey', currentPrice);
      await this.$store.dispatch('config/updateRatePlanPrices');
      //   gtmViewItemListEvent(); // TODO: track event only if the list does not change (single/u30)
    },
  },
};
</script>

<style lang="scss" scoped>
.subnavigation-switch {
  $switchWidth: 194px;

  .switcher {
    width: $switchWidth;

    input {
      appearance: none;
      width: $switchWidth;
      outline: none;
      transition: 0.25s -0.1s;

      &:not(:checked) {
        & + label {
          left: 8px;
          right: 100px;
          background: $color-almost-black;
          transition: left 0.5s, right 0.4s 0.2s;
          width: 80px;
          transition: left 0.4s 0.1s, right 0.5s, background 0.35s 0s;
        }
      }

      &:checked {
        transition: background 0.5s -0.1s;

        &:before {
          color: #{$color-black};
          transition: color 0.5s;
        }

        &:after {
          color: white;
          transition: color 0.5s 0.2s;
        }

        & + label {
          left: 96px;
          right: 8px;
          background: $color-almost-black;
          transition: left 0.4s 0.1s, right 0.5s, background 0.35s 0.5s;
        }
      }

      &:before,
      &:after {
        z-index: 2;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        color: white;
        white-space: nowrap;
        font-size: 17.5px;
        line-break: normal;
        font-family: $font-normal !important;
      }

      &:before {
        color: white;
        transition: color 0.5s 0.2s;
        white-space: nowrap;
        content: attr(data-first);
        left: 20px;
      }

      &:after {
        border: none;
        background: transparent;
        color: black;
        transition: color 0.5s;
        white-space: nowrap;
        content: attr(data-second);
        right: 16px;
      }
    }

    label {
      z-index: 1;
      position: absolute;
      top: 6px;
      bottom: 6px;
      height: 44px;
    }
  }
}

.multi-switch {
  margin-top: $spacing-s;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  @include desktop {
    margin-top: $spacing-m;
  }

  button {
    font-family: $font-normal;
    cursor: pointer;
    border: none;
    background-color: $color-white;
    border-radius: 30px;
    padding: 8px 12px;
    width: 98px;
    @include transition(0.3s);

    &[aria-pressed='true'] {
      background-color: $color-almost-black;
      color: $color-white;
      @include box-shadow(0 4px 4px 0 rgba(0, 0, 0, 0.25));
    }
  }
}
</style>
